import { supabase } from '../lib/supabase';

export const adminService = {
  // Get all suppliers with tier information
  async getAllSuppliers() {
    try {
      const { data, error } = await supabase
        ?.from('suppliers')
        ?.select(`
          *,
          user:user_profiles!suppliers_user_id_fkey(
            full_name,
            email,
            phone
          )
        `)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get platform-wide analytics
  async getPlatformAnalytics() {
    try {
      // Get supplier counts by tier
      const { data: suppliers, error: suppliersError } = await supabase
        ?.from('suppliers')
        ?.select('total_orders, rating, country, currency');

      if (suppliersError) throw suppliersError;

      // Calculate tier distribution
      const tierDistribution = {
        bronze: 0,
        silver: 0,
        gold: 0,
        platinum: 0
      };

      const revenueByTier = {
        bronze: 0,
        silver: 0,
        gold: 0,
        platinum: 0
      };

      suppliers?.forEach((supplier) => {
        const revenue = (supplier?.total_orders || 0) * 15;
        const tier = this.calculateTier(revenue);
        tierDistribution[tier] = (tierDistribution?.[tier] || 0) + 1;
        revenueByTier[tier] = (revenueByTier?.[tier] || 0) + revenue;
      });

      // Get total orders
      const { count: totalOrders } = await supabase
        ?.from('orders')
        ?.select('*', { count: 'exact', head: true });

      // Get total revenue
      const { data: orders } = await supabase
        ?.from('orders')
        ?.select('total_amount');

      const totalRevenue = orders?.reduce((sum, order) => sum + parseFloat(order?.total_amount || 0), 0) || 0;

      return {
        data: {
          totalSuppliers: suppliers?.length || 0,
          tierDistribution,
          revenueByTier,
          totalOrders: totalOrders || 0,
          totalRevenue,
          averageRating: suppliers?.reduce((sum, s) => sum + (parseFloat(s?.rating) || 0), 0) / (suppliers?.length || 1)
        },
        error: null
      };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Calculate tier based on revenue
  calculateTier(revenue) {
    if (revenue >= 150000) return 'platinum';
    if (revenue >= 75000) return 'gold';
    if (revenue >= 25000) return 'silver';
    return 'bronze';
  },

  // Get tier thresholds configuration
  async getTierThresholds() {
    try {
      const { data, error } = await supabase
        ?.from('admin_settings')
        ?.select('*')
        ?.eq('setting_key', 'tier_thresholds')
        ?.single();

      if (error && error?.code !== 'PGRST116') throw error;

      // Return default thresholds if not found
      if (!data) {
        return {
          data: {
            bronze: 0,
            silver: 25000,
            gold: 75000,
            platinum: 150000
          },
          error: null
        };
      }

      return { data: data?.setting_value, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Update tier thresholds
  async updateTierThresholds(thresholds) {
    try {
      const { data, error } = await supabase
        ?.from('admin_settings')
        ?.upsert({
          setting_key: 'tier_thresholds',
          setting_value: thresholds,
          updated_at: new Date()?.toISOString()
        }, { onConflict: 'setting_key' })
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get supported countries
  async getSupportedCountries() {
    try {
      const { data, error } = await supabase
        ?.from('admin_settings')
        ?.select('*')
        ?.eq('setting_key', 'supported_countries')
        ?.single();

      if (error && error?.code !== 'PGRST116') throw error;

      // Return default countries if not found
      if (!data) {
        return {
          data: [
            { value: 'ie', label: 'Ireland' },
//            { value: 'uk', label: 'United Kingdom' },
//            { value: 'ca', label: 'Canada' },
//            { value: 'au', label: 'Australia' },
//            { value: 'in', label: 'India' },
//            { value: 'ae', label: 'United Arab Emirates' },
//            { value: 'sg', label: 'Singapore' },
//            { value: 'my', label: 'Malaysia' },
//            { value: 'nz', label: 'New Zealand' },
//            { value: 'za', label: 'South Africa' }
          ],
          error: null
        };
      }

      return { data: data?.setting_value, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Update supported countries
  async updateSupportedCountries(countries) {
    try {
      const { data, error } = await supabase
        ?.from('admin_settings')
        ?.upsert({
          setting_key: 'supported_countries',
          setting_value: countries,
          updated_at: new Date()?.toISOString()
        }, { onConflict: 'setting_key' })
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get supported currencies
  async getSupportedCurrencies() {
    try {
      const { data, error } = await supabase
        ?.from('admin_settings')
        ?.select('*')
        ?.eq('setting_key', 'supported_currencies')
        ?.single();

      if (error && error?.code !== 'PGRST116') throw error;

      // Return default currencies if not found
      if (!data) {
        return {
          data: [
            { value: 'usd', label: 'USD - US Dollar ($)' },
            { value: 'gbp', label: 'GBP - British Pound (£)' },
            { value: 'eur', label: 'EUR - Euro (€)' },
            { value: 'cad', label: 'CAD - Canadian Dollar (C$)' },
            { value: 'aud', label: 'AUD - Australian Dollar (A$)' },
            { value: 'inr', label: 'INR - Indian Rupee (₹)' },
            { value: 'aed', label: 'AED - UAE Dirham (د.إ)' },
            { value: 'sgd', label: 'SGD - Singapore Dollar (S$)' },
            { value: 'myr', label: 'MYR - Malaysian Ringgit (RM)' },
            { value: 'nzd', label: 'NZD - New Zealand Dollar (NZ$)' }
          ],
          error: null
        };
      }

      return { data: data?.setting_value, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Update supported currencies
  async updateSupportedCurrencies(currencies) {
    try {
      const { data, error } = await supabase
        ?.from('admin_settings')
        ?.upsert({
          setting_key: 'supported_currencies',
          setting_value: currencies,
          updated_at: new Date()?.toISOString()
        }, { onConflict: 'setting_key' })
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get suppliers approaching tier upgrades
  async getSuppliersNearTierUpgrade() {
    try {
      const { data: suppliers, error } = await supabase
        ?.from('suppliers')
        ?.select(`
          *,
          user:user_profiles!suppliers_user_id_fkey(
            full_name,
            email
          )
        `);

      if (error) throw error;

      const thresholdsResult = await this.getTierThresholds();
      const thresholds = thresholdsResult?.data || { bronze: 0, silver: 25000, gold: 75000, platinum: 150000 };

      const nearUpgrade = suppliers?.filter((supplier) => {
        const revenue = (supplier?.total_orders || 0) * 15;
        const currentTier = this.calculateTier(revenue);
        
        let nextThreshold;
        if (currentTier === 'bronze') nextThreshold = thresholds?.silver;
        else if (currentTier === 'silver') nextThreshold = thresholds?.gold;
        else if (currentTier === 'gold') nextThreshold = thresholds?.platinum;
        else return false;

        const progress = (revenue / nextThreshold) * 100;
        return progress >= 75 && progress < 100;
      });

      return { data: nearUpgrade, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get revenue trends (last 6 months)
  async getRevenueTrends() {
    try {
      const sixMonthsAgo = new Date();
      sixMonthsAgo?.setMonth(sixMonthsAgo?.getMonth() - 6);

      const { data, error } = await supabase
        ?.from('orders')
        ?.select('total_amount, created_at')
        ?.gte('created_at', sixMonthsAgo?.toISOString())
        ?.order('created_at', { ascending: true });

      if (error) throw error;

      // Group by month
      const monthlyData = {};
      data?.forEach((order) => {
        const month = new Date(order?.created_at)?.toLocaleString('en-US', { month: 'short' });
        if (!monthlyData?.[month]) {
          monthlyData[month] = { revenue: 0, orders: 0 };
        }
        monthlyData[month].revenue += parseFloat(order?.total_amount || 0);
        monthlyData[month].orders += 1;
      });

      const trends = Object.entries(monthlyData)?.map(([month, data]) => ({
        month,
        revenue: data?.revenue,
        orders: data?.orders
      }));

      return { data: trends, error: null };
    } catch (error) {
      return { data: [], error };
    }
  }
};